<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>
  <div class="page-wrapper">
    <div class="content">
      <!-- Page Header -->
      <div class="page-header mb-4">
        <div class="add-item d-flex">
          <div class="page-title">
            <h4>Dashboard</h4>
            <h6>Selamat datang di Sundava Agro Indonesia</h6>
          </div>
        </div>
      </div>

      <!-- Desktop View (Hidden on Mobile) -->
      <div class="d-none d-lg-block">
        <!-- Summary Cards -->
        <div class="row">
          <div v-if="$hasPermission('ssp', 'members', 'view')" class="col-xl-3 col-sm-6 col-12 d-flex">
            <div class="dash-count">
              <div class="dash-counts">
                <h4>{{ stats.total_kios }}</h4>
                <h5>Total Kios</h5>
              </div>
              <div class="dash-imgs">
                <vue-feather type="users"></vue-feather>
              </div>
            </div>
          </div>
          <div v-if="$hasPermission('ssp', 'products', 'view')" class="col-xl-3 col-sm-6 col-12 d-flex">
            <div class="dash-count das1">
              <div class="dash-counts">
                <h4>{{ stats.total_products }}</h4>
                <h5>Total Produk</h5>
              </div>
              <div class="dash-imgs">
                <vue-feather type="box"></vue-feather>
              </div>
            </div>
          </div>
          <div v-if="$hasPermission('ssp', 'orders', 'view')" class="col-xl-3 col-sm-6 col-12 d-flex">
            <div class="dash-count das2">
              <div class="dash-counts">
                <h4>{{ stats.total_orders }}</h4>
                <h5>Total PO</h5>
              </div>
              <div class="dash-imgs">
                <vue-feather type="shopping-cart"></vue-feather>
              </div>
            </div>
          </div>
          <div v-if="$hasPermission('ssp', 'orders', 'view')" class="col-xl-3 col-sm-6 col-12 d-flex">
            <div class="dash-count das3">
              <div class="dash-counts">
                <h4>{{ stats.completed_orders }}</h4>
                <h5>PO Selesai</h5>
              </div>
              <div class="dash-imgs">
                <vue-feather type="check-circle"></vue-feather>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="row mt-4 d-none d-lg-flex">
        <!-- Kios Map -->
        <div class="col-lg-8">
          <div class="card">
            <div class="card-body p-0">
              <div id="kios-map" :class="{ 'full-screen-map': isFullScreen }" style="height: 480px; border-radius: 12px; background: #f8f9fa; transition: all 0.3s ease; position: relative;">
                <!-- Floating Control Button -->
                <div class="map-controls">
                  <button @click="toggleFullScreen" class="btn shadow-sm d-flex align-items-center" :class="isFullScreen ? 'btn-danger' : 'btn-white'">
                    <vue-feather :type="isFullScreen ? 'x' : 'maximize'" class="me-1" style="width: 16px;"></vue-feather>
                    <span>{{ isFullScreen ? 'Tutup Map' : 'Full Screen' }}</span>
                  </button>
                </div>

                <l-map 
                  ref="mapRef" 
                  v-if="mapCenter"
                  :zoom="mapZoom" 
                  :center="mapCenter" 
                  :bounds="mapBounds"
                  :bounds-padding="[40, 40]"
                  :use-global-leaflet="false"
                  style="height: 100%; width: 100%;"
                >
                  <l-tile-layer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"></l-tile-layer>
                  <l-marker 
                    v-for="kios in kiosWithCoords" 
                    :key="kios.id" 
                    :lat-lng="{lat: parseFloat(kios.latitude), lng: parseFloat(kios.longitude)}"
                  >
                    <l-icon 
                      :icon-size="[36, 36]"
                      :icon-anchor="[18, 18]"
                      :popup-anchor="[0, -18]"
                      class-name="custom-kios-marker"
                    >
                      <div class="marker-circle">
                        <i class="fa fa-store"></i>
                      </div>
                    </l-icon>
                    <l-popup :options="{ maxWidth: 280, className: 'kios-popup' }">
                      <div class="kios-popup-content">
                        <div v-if="kios.foto_toko" class="popup-image-container">
                          <img :src="getImageUrl(kios.foto_toko)" class="popup-image" alt="Foto Toko">
                        </div>
                        <div class="popup-body">
                          <h6 class="popup-title">{{ kios.nama_toko }}</h6>
                          <div class="popup-info no-icon">
                            <span>{{ kios.nama_member }}</span>
                          </div>
                          <div class="popup-info no-icon mt-1">
                            <span>{{ kios.alamat || '-' }}</span>
                          </div>
                          <router-link :to="'/people/kios-details/' + kios.id" class="btn btn-success btn-sm w-100 mt-2">
                            Lihat Detail
                          </router-link>
                        </div>
                      </div>
                    </l-popup>
                  </l-marker>
                </l-map>
              </div>
            </div>
          </div>
        </div>

        <div v-if="$hasPermission('ssp', 'orders', 'view')" class="col-lg-4">
          <!-- Order Status Summary -->
          <div class="card">
            <div class="card-header border-0 bg-white">
              <h5 class="card-title fw-bold mb-0">Status PO</h5>
            </div>
            <div class="card-body pt-0">
              <div class="mb-3">
                <div class="d-flex justify-content-between mb-1">
                  <span>Processing</span>
                  <span class="fw-bold">{{ stats.processing_orders }}</span>
                </div>
                <div class="progress" style="height: 8px;">
                  <div class="progress-bar bg-dark" :style="{ width: getPercentage('processing') + '%' }"></div>
                </div>
              </div>
              <div class="mb-3">
                <div class="d-flex justify-content-between mb-1">
                  <span>Partial</span>
                  <span class="fw-bold">{{ stats.partial_orders }}</span>
                </div>
                <div class="progress" style="height: 8px;">
                  <div class="progress-bar bg-warning" :style="{ width: getPercentage('partial') + '%' }"></div>
                </div>
              </div>
              <div class="mb-3">
                <div class="d-flex justify-content-between mb-1">
                  <span>Completed</span>
                  <span class="fw-bold">{{ stats.completed_orders }}</span>
                </div>
                <div class="progress" style="height: 8px;">
                  <div class="progress-bar bg-success" :style="{ width: getPercentage('completed') + '%' }"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Top Kiosks Section (Desktop Only) -->
      <div v-if="$hasPermission('ssp', 'orders', 'view')" class="row mt-4 d-none d-lg-flex">
        <div class="col-12">
          <div class="card shadow-sm border-0" style="border-radius: 12px; overflow: hidden;">
            <div class="card-header bg-white border-0 py-3 d-flex justify-content-between align-items-center">
              <h5 class="card-title mb-0 d-flex align-items-center fw-bold">
                <vue-feather type="award" class="me-2 text-warning" style="width: 20px;"></vue-feather>
                Kios Terbanyak PO
              </h5>
              <small class="text-muted">Top 5 Performa Kios</small>
            </div>
            <div class="card-body p-0">
              <div class="table-responsive">
                <table class="table table-hover mb-0 align-middle custom-dash-table">
                  <thead>
                    <tr class="bg-light bg-opacity-50">
                      <th class="ps-4 py-3" style="width: 80px;">Rank</th>
                      <th class="py-3">Info Kios</th>
                      <th class="py-3">Pemilik</th>
                      <th class="py-3 text-center">Total PO</th>
                      <th class="py-3 text-end pe-4" style="width: 150px;">Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(kios, index) in topKiosks" :key="kios.id">
                      <td class="ps-4">
                        <span :class="['rank-badge', 'rank-' + (index + 1)]">{{ index + 1 }}</span>
                      </td>
                      <td>
                        <div class="d-flex align-items-center">
                          <div class="store-icon me-2">
                             <vue-feather type="shopping-bag" style="width: 14px;"></vue-feather>
                          </div>
                          <span class="fw-bold text-dark">{{ kios.nama_toko }}</span>
                        </div>
                      </td>
                      <td>{{ kios.nama_member }}</td>
                      <td class="text-center">
                        <span class="badge rounded-pill bg-primary bg-opacity-10 text-primary px-3 py-2 fw-bold" style="font-size: 13px;">
                           {{ kios.total_po }} PO
                        </span>
                      </td>
                      <td class="text-end pe-4">
                        <router-link :to="'/people/kios-details/' + kios.id" class="btn btn-sm btn-outline-success rounded-pill px-3">
                           Detail <vue-feather type="chevron-right" style="width: 14px;"></vue-feather>
                        </router-link>
                      </td>
                    </tr>
                    <tr v-if="topKiosks.length === 0">
                       <td colspan="5" class="text-center py-4 text-muted small">Belum ada data transaksi</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ============================================== -->
      <!-- MOBILE DASHBOARD (Fintech Style) -->
      <!-- ============================================== -->
      <div class="mobile-dashboard d-lg-none pb-5">
        <!-- New Mobile Header (Native Style) -->
        <div class="mob-native-header d-flex justify-content-between align-items-center mb-4">
           <div class="d-flex align-items-center gap-2">
              <div class="header-avatar shadow-sm">
                 {{ (currentUser?.full_name || 'A').charAt(0) }}
              </div>
              <div class="header-welcome">
                 <span class="text-muted smallest fw-bold d-block lh-1">Hello,</span>
                 <h5 class="fw-bold mb-0 text-dark">{{ currentUser?.full_name || 'User' }}</h5>
              </div>
           </div>
           <div @click="handleLogout" class="header-logout-circle shadow-sm">
              <vue-feather type="log-out" class="text-danger" size="18"></vue-feather>
           </div>
        </div>

        <!-- Services Grid (Expanded 4x2) -->
        <div class="mt-2 mb-5 px-1">
           <h6 class="fw-bold text-dark mb-3">Layanan Utama</h6>
           <div class="row g-3">
              <!-- Row 1 -->
              <div v-if="$hasPermission('ssp', 'orders', 'create')" class="col-3 text-center">
                 <router-link to="/purchases/add-order" class="service-icon-link">
                    <div class="service-box bg-green-soft"><vue-feather type="plus-square" class="text-green" size="20"></vue-feather></div>
                    <span class="service-label">Buat PO</span>
                 </router-link>
              </div>
              <div v-if="$hasPermission('ssp', 'orders', 'view')" class="col-3 text-center">
                 <router-link to="/purchases/order-list" class="service-icon-link">
                    <div class="service-box bg-blue-soft"><vue-feather type="clipboard" class="text-blue" size="20"></vue-feather></div>
                    <span class="service-label">Daftar PO</span>
                 </router-link>
              </div>
              <div v-if="$hasPermission('ssp', 'members', 'view')" class="col-3 text-center">
                 <router-link to="/dashboard/map-mobile" class="service-icon-link">
                    <div class="service-box bg-purple-soft"><vue-feather type="map-pin" class="text-purple" size="20"></vue-feather></div>
                    <span class="service-label">Map Kios</span>
                 </router-link>
              </div>
              <div v-if="$hasPermission('ssp', 'orders', 'view')" class="col-3 text-center">
                 <router-link to="/purchases/order-calendar" class="service-icon-link">
                    <div class="service-box bg-red-soft"><vue-feather type="calendar" class="text-red" size="20"></vue-feather></div>
                    <span class="service-label">Kalender</span>
                 </router-link>
              </div>
              
              <!-- Row 2 -->
              <div v-if="$hasPermission('ssp', 'members', 'view')" class="col-3 text-center">
                 <router-link to="/people/kios-list" class="service-icon-link">
                    <div class="service-box bg-orange-soft"><vue-feather type="home" class="text-orange" size="20"></vue-feather></div>
                    <span class="service-label">Kios SSP</span>
                 </router-link>
              </div>
              <div v-if="$hasPermission('ssp', 'sources', 'view')" class="col-3 text-center">
                 <router-link to="/inventory/source-list" class="service-icon-link">
                    <div class="service-box bg-indigo-soft"><vue-feather type="truck" class="text-indigo" size="20"></vue-feather></div>
                    <span class="service-label">Sumber</span>
                 </router-link>
              </div>
              <div v-if="$hasPermission('ssp', 'pendampings', 'view')" class="col-3 text-center">
                 <router-link to="/people/pendamping-list" class="service-icon-link">
                    <div class="service-box bg-teal-soft"><vue-feather type="users" class="text-teal" size="20"></vue-feather></div>
                    <span class="service-label">Petugas</span>
                 </router-link>
              </div>
              <div v-if="$hasPermission('ssp', 'products', 'view')" class="col-3 text-center">
                 <router-link to="/inventory/product-list" class="service-icon-link">
                    <div class="service-box bg-pink-soft"><vue-feather type="package" class="text-pink" size="20"></vue-feather></div>
                    <span class="service-label">Produk</span>
                 </router-link>
              </div>
           </div>
        </div>

        <!-- Recent Content (Fintech List Style) -->
        <div class="mt-5 px-1">
           <div class="d-flex justify-content-between align-items-center mb-3">
              <h6 class="fw-bold text-dark mb-0">Kios Unggulan</h6>
              <router-link to="/people/kios-list" class="smallest fw-bold text-decoration-none" style="color: #029147;">LIHAT SEMUA</router-link>
           </div>
           
           <div class="mob-fintech-list">
              <div v-for="(kios, idx) in topKiosks.slice(0, 4)" :key="'mob-kios-'+kios.id" class="fin-list-item d-flex align-items-center gap-3 p-3 bg-white shadow-xs rounded-4 mb-2 border border-light-dark">
                 <div class="fin-item-icon" :style="{ backgroundColor: idx === 0 ? '#fee2e2' : (idx === 1 ? '#dcfce7' : '#fef9c3') }">
                    <vue-feather :type="idx === 0 ? 'award' : 'shopping-bag'" :class="idx === 0 ? 'text-red' : (idx === 1 ? 'text-green' : 'text-orange')" size="18"></vue-feather>
                 </div>
                 <div class="flex-fill overflow-hidden">
                    <h6 class="fw-bold mb-0 text-dark text-truncate">{{ kios.nama_toko }}</h6>
                    <span class="smallest text-muted fw-bold uppercase">{{ kios.nama_member }}</span>
                 </div>
                 <div class="text-end">
                    <h5 class="fw-bold text-dark mb-0">+{{ kios.total_po }}</h5>
                    <span class="smallest text-muted fw-bold">ORDER</span>
                 </div>
              </div>
           </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useDataStore } from '@/store/dataStore';
import { LMap, LTileLayer, LMarker, LIcon, LPopup } from '@vue-leaflet/vue-leaflet';
import 'leaflet/dist/leaflet.css';
import authService from "@/services/authService";

export default {
  components: {
    LMap,
    LTileLayer,
    LMarker,
    LIcon,
    LPopup
  },
  setup() {
    const router = useRouter();
    const { orders, members, products, loadAllData } = useDataStore();
    const mapZoom = ref(10);
    const mapCenter = ref([-7.4313, 109.2478]); // Default center (Banyumas/Purwokerto)
    const isFullScreen = ref(false);
    const currentUser = ref(null);
    
    const toggleFullScreen = () => {
      isFullScreen.value = !isFullScreen.value;
      // Invalidate map size after animation
      setTimeout(() => {
        if (mapRef.value && mapRef.value.leafletObject) {
          mapRef.value.leafletObject.invalidateSize();
        }
      }, 350);
    };

    // Exit full screen with Escape key
    const handleEscKey = (e) => {
      if (e.key === 'Escape' && isFullScreen.value) {
        isFullScreen.value = false;
        toggleFullScreen();
      }
    };
    
    onMounted(() => {
      window.addEventListener('keydown', handleEscKey);
    });
    const apiBaseUrl = process.env.VUE_APP_API_URL || '';

    const stats = computed(() => {
      const allOrders = orders.value || [];
      let processing = 0, partial = 0, completed = 0;

      allOrders.forEach(o => {
        const shipped = o.shipped_qty || 0;
        const total = o.total_qty || 0;
        if (shipped === 0) processing++;
        else if (shipped < total) partial++;
        else if (shipped >= total && total > 0) completed++;
      });

      return {
        total_kios: (members.value || []).length,
        total_products: (products.value || []).length,
        total_orders: allOrders.length,
        processing_orders: processing,
        partial_orders: partial,
        completed_orders: completed
      };
    });

    const topKiosks = computed(() => {
        const kioskPO = {};
        (orders.value || []).forEach(o => {
            const memberId = o.member_id;
            if (!memberId) return;
            kioskPO[memberId] = (kioskPO[memberId] || 0) + 1;
        });

        return Object.keys(kioskPO)
            .map(id => {
                const member = (members.value || []).find(m => m.id == id);
                return {
                    id,
                    nama_toko: member ? member.nama_toko : 'Kios Tidak Dikenal',
                    nama_member: member ? member.nama_member : '-',
                    total_po: kioskPO[id],
                };
            })
            .sort((a, b) => b.total_po - a.total_po)
            .slice(0, 5); // Top 5
    });

    const kiosWithCoords = computed(() => {
        const filtered = (members.value || []).filter(m => {
            const lat = parseFloat(m.latitude);
            const lng = parseFloat(m.longitude);
            return !isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0;
        });
        console.log('Map debug - Members with valid coordinates:', filtered.length);
        return filtered;
    });

    const mapBounds = computed(() => {
        if (kiosWithCoords.value.length === 0) return null;
        
        const coords = kiosWithCoords.value.map(k => [parseFloat(k.latitude), parseFloat(k.longitude)]);
        return coords;
    });

    const getImageUrl = (path) => {
      if (!path) return '';
      if (path.startsWith('http')) return path;
      const formattedPath = path.startsWith('/') ? path : `/${path}`;
      return `${apiBaseUrl}${formattedPath}`;
    };

    const formatDate = (dateStr) => {
      if (!dateStr) return '-';
      return new Date(dateStr).toLocaleDateString('id-ID', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    };

    const getPercentage = (type) => {
      const total = stats.value.total_orders || 1;
      if (type === 'processing') return (stats.value.processing_orders / total) * 100;
      if (type === 'partial') return (stats.value.partial_orders / total) * 100;
      if (type === 'completed') return (stats.value.completed_orders / total) * 100;
      return 0;
    };

    const mapRef = ref(null);

    // Watch for bounds change and apply fitBounds manually for better reliability
    watch(mapBounds, (newBounds) => {
      if (newBounds && newBounds.length > 0 && mapRef.value) {
        console.log('Map - Fitting bounds for markers:', newBounds.length);
        // Sometimes the map object needs a small delay to be ready
        setTimeout(() => {
            if (mapRef.value && mapRef.value.leafletObject) {
                mapRef.value.leafletObject.fitBounds(newBounds, { padding: [50, 50] });
            }
        }, 100);
      }
    }, { deep: true });

    const handleLogout = () => {
      authService.logout();
      router.push('/');
    };

    onMounted(async () => {
      await loadAllData();
      
      const session = localStorage.getItem('currentUser');
      if (session) {
          currentUser.value = JSON.parse(session);
      }

      if (members.value.length > 0) {
          console.log('Dashboard - First member sample:', JSON.stringify(members.value[0]));
      }

      // Initial fit if data already there
      if (mapBounds.value && mapBounds.value.length > 0 && mapRef.value) {
          setTimeout(() => {
              if (mapRef.value && mapRef.value.leafletObject) {
                  mapRef.value.leafletObject.fitBounds(mapBounds.value, { padding: [50, 50] });
              }
          }, 500);
      }
    });

    return {
      stats,
      kiosWithCoords,
      mapBounds,
      mapZoom,
      mapCenter,
      mapRef,
      getImageUrl,
      formatDate,
      getPercentage,
      topKiosks,
      isFullScreen,
      toggleFullScreen,
      currentUser,
      handleLogout
    };
  }
};
</script>

<style scoped>
.dash-count {
    margin-bottom: 20px;
}
#kios-map {
    border: 1px solid #f0f0f0;
    overflow: hidden;
    z-index: 1;
    position: relative;
}

.full-screen-map {
    position: fixed !important;
    top: 0;
    left: 0;
    width: 100vw !important;
    height: 100vh !important;
    z-index: 9999 !important;
    border-radius: 0 !important;
}

.map-controls {
    position: absolute;
    top: 15px;
    right: 15px;
    z-index: 10000;
}

.btn-white {
    background: white;
    color: #333;
    border: 1px solid #ddd;
}
.btn-white:hover {
    background: #f8f9fa;
    color: #000;
}

/* Custom Popup Styling */
:deep(.kios-popup) .leaflet-popup-content-wrapper {
    padding: 0;
    overflow: hidden;
    border-radius: 12px;
}
:deep(.kios-popup) .leaflet-popup-content {
    margin: 0;
    width: 250px !important;
}
.kios-popup-content {
    display: flex;
    flex-direction: column;
}
.popup-image-container {
    height: 120px;
    width: 100%;
    overflow: hidden;
    background: #f0f0f0;
}
.popup-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
}
.popup-body {
    padding: 12px;
}
.popup-title {
    font-weight: 700;
    margin-bottom: 8px;
    color: #333;
    font-size: 14px;
    text-transform: uppercase;
}
.popup-info {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: #444;
}
.popup-info.no-icon {
    padding-left: 2px;
}
.popup-info span {
    white-space: normal;
    word-break: break-word;
    line-height: 1.4;
}

/* Table Design Update */
.custom-dash-table thead th {
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #777;
    border: none;
}
.custom-dash-table tbody tr {
    transition: all 0.2s;
}
.custom-dash-table tbody tr:hover {
    background-color: #f8faff !important;
}
.custom-dash-table td {
    border-bottom: 1px solid #f0f0f0;
    font-size: 14px;
    padding-top: 15px;
    padding-bottom: 15px;
}

.rank-badge {
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    font-size: 11px;
    font-weight: 800;
}
.rank-1 { background: #ffd700; color: #000; }
.rank-2 { background: #c0c0c0; color: #000; }
.rank-3 { background: #cd7f32; color: #fff; }
.rank-4, .rank-5 { background: #f1f3f5; color: #666; }

.store-icon {
    width: 28px;
    height: 28px;
    background: #eef2f7;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #6c757d;
}

/* Circular Marker Style */
:deep(.custom-kios-marker) {
    background: transparent;
    border: none;
}
.marker-circle {
    width: 36px;
    height: 36px;
    background-color: #28a745;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    border: 3px solid #ffffff;
    box-shadow: 0 3px 6px rgba(0,0,0,0.3);
    transition: transform 0.2s;
}
.marker-circle:hover {
    transform: scale(1.1);
    background-color: #218838;
}
.marker-circle i {
    font-size: 16px;
}

/* Mobile Dashboard Fintech Styles */
.mobile-dashboard {
  padding: 20px 16px;
  background: #fdfdfd;
}

.mob-native-header .header-avatar {
  width: 44px;
  height: 44px;
  background: #029147;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 18px;
}

.header-logout-circle {
  width: 40px;
  height: 40px;
  background: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ef4444;
  border: 1px solid #fee2e2;
  cursor: pointer;
}
.header-logout-circle:active {
  background: #fff5f5;
  transform: scale(0.95);
}

.lush-green-card {
  background: linear-gradient(135deg, #029147 0%, #036d36 100%);
  border-radius: 28px;
  position: relative;
  overflow: hidden;
  min-height: 160px;
}

.shadow-green {
  box-shadow: 0 15px 30px rgba(2, 145, 71, 0.25);
}

.lush-decor-1 {
  position: absolute;
  width: 120px;
  height: 120px;
  background: rgba(255,255,255,0.1);
  border-radius: 50%;
  top: -30px;
  right: -20px;
}

.lush-decor-2 {
  position: absolute;
  width: 80px;
  height: 80px;
  background: rgba(255,255,255,0.05);
  border-radius: 50%;
  bottom: -20px;
  left: 20px;
}

.uppercase { text-transform: uppercase; letter-spacing: 0.5px; }

/* Services */
.service-icon-link {
  text-decoration: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.service-box {
  width: 54px;
  height: 54px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s;
}

.service-icon-link:active .service-box {
  transform: scale(0.9);
}

.service-label {
  font-size: 11px;
  font-weight: 700;
  color: #475569;
}

.bg-red-soft { background: #fee2e2; } .text-red { color: #ef4444; }
.bg-blue-soft { background: #e0f2fe; } .text-blue { color: #0ea5e9; }
.bg-orange-soft { background: #ffedd5; } .text-orange { color: #f59e0b; }
.bg-green-soft { background: #dcfce7; } .text-green { color: #10b981; }
.bg-purple-soft { background: #f3e8ff; } .text-purple { color: #a855f7; }
.bg-indigo-soft { background: #e0e7ff; } .text-indigo { color: #6366f1; }
.bg-teal-soft { background: #ccfbf1; } .text-teal { color: #0d9488; }
.bg-pink-soft { background: #fce7f3; } .text-pink { color: #db2777; }

/* Fintech List */
.fin-list-item {
  border: 1px solid #f8fafc;
  transition: background 0.2s;
}

.fin-list-item:active {
  background: #f8fafc;
}

.fin-item-icon {
  width: 44px;
  height: 44px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.shadow-xs {
  box-shadow: 0 2px 8px rgba(0,0,0,0.02);
}

.border-light-dark {
   border-color: rgba(0,0,0,0.03) !important;
}

@media (max-width: 991.98px) {
  .page-header {
    display: none !important;
  }
  .content {
    padding: 0 !important;
  }
}
</style>
